/**
 * Panneau de classement — l'onglet Classements de Progression (§15, ROADMAP 7.1).
 *
 * ══ Ce qu'il montre, et pourquoi dans cet ordre ══════════════════════════════
 *
 * D'abord **la place du joueur**, ensuite les cinquante premiers. L'inverse aurait été
 * plus simple à écrire et beaucoup moins utile : un joueur qui arrive 900e et voit d'abord
 * un top 50 sans lui apprend qu'il ne compte pas. Sa place est la seule ligne qui parle de
 * lui, elle vient donc en premier.
 *
 * ══ Les trois états, tenus séparés ═══════════════════════════════════════════
 *
 * ⚠️ Ce panneau a trois façons de ne rien afficher, et les confondre serait le défaut
 * classique de ce projet — un échec qui ressemble à une donnée :
 *
 *  · **hors ligne / pas de configuration** — on ne sait rien, on le dit ;
 *  · **le calcul n'a jamais tourné** — toutes les villes sont à zéro. Sans ce cas, l'écran
 *    afficherait un classement vide, exactement comme si le jeu n'avait aucun joueur ;
 *  · **le joueur n'est pas classé** — sa ville ne produit encore rien. Ce n'est pas une
 *    erreur, c'est un début de partie, et la phrase doit dire quoi faire.
 *
 * ══ Ce qu'il ne fait PAS (règle 17) ══════════════════════════════════════════
 *
 * Il ne se rafraîchit pas tout seul. Le classement bouge à la vitesse de la passe
 * serveur — quelques fois par jour — et une liste qui se réécrit sous les yeux du joueur
 * lui ferait croire à un direct qui n'existe pas. Un bouton le recharge, et l'heure de
 * lecture est affichée.
 */

import { RENAME_REWARD_GEMS } from '@/core/cityNaming';
import { n, t } from '@/i18n';
import { fetchLeaderboard } from '@/net/leaderboard';
import type { Leaderboard } from '@/net/leaderboard';
import { createButton, createPanel } from './components';
import { icon } from './icons';

export interface RankingPanelHandle {
  readonly el: HTMLElement;
  /** Recharge depuis le serveur. Appelé à l'ouverture de l'onglet, et par le bouton. */
  refresh(ownSlug: string | null): void;
  /**
   * Force l'un des trois états vides, pour la sonde (`?debug`).
   *
   * ⚠️ Sans cette porte, une sonde ne peut mesurer que l'état qu'elle rencontre par
   * hasard — en pratique toujours le même, la ville de sonde étant vide. Elle validerait
   * alors « l'écran affiche quelque chose » et raterait le seul défaut qui compte ici :
   * que les trois états disent la MÊME chose (règle 23, le cas absent de l'état de mesure).
   */
  showState(state: 'offline' | 'pending' | 'unranked' | 'unnamed'): void;
}

export function createRankingPanel(): RankingPanelHandle {
  const title = document.createElement('h3');
  title.className = 'ranking__title';
  title.append(icon('trophy'), t('ranking.title'));

  const own = document.createElement('p');
  own.className = 'ranking__own';

  const note = document.createElement('p');
  note.className = 'ranking__note';

  const list = document.createElement('ol');
  list.className = 'ranking__list';

  const reload = createButton({
    label: t('ranking.reload'),
    variant: 'ghost',
    icon: 'rotate',
  });

  const panel = createPanel({ children: [title, own, note, list, reload.el] });
  panel.el.classList.add('ranking');

  let lastSlug: string | null = null;
  let loading = false;

  /** Un état, une phrase. Jamais deux états dans le même écran. */
  const say = (text: string, hideList = true): void => {
    note.textContent = text;
    note.hidden = text === '';
    if (hideList) list.replaceChildren();
    list.hidden = hideList;
  };

  const render = (board: Leaderboard | null, slug: string | null): void => {
    if (board === null) {
      own.textContent = '';
      own.hidden = true;
      say(t('ranking.offline'));
      return;
    }

    if (board.notComputed) {
      own.textContent = '';
      own.hidden = true;
      say(t('ranking.pending'));
      return;
    }

    if (board.ownUnnamed) {
      /*
        Un QUATRIÈME état, et il se distingue des trois autres : le joueur produit peut-être
        beaucoup, il n'est simplement pas éligible. Lui afficher « ta ville n'est pas encore
        classée, pose ton premier bâtiment » serait faux et le laisserait sans recours.
      */
      own.textContent = '';
      own.hidden = true;
      say(t('ranking.unnamed', { value: n(RENAME_REWARD_GEMS) }), false);
    } else if (board.ownRank === null) {
      own.textContent = '';
      own.hidden = true;
      say(t('ranking.unranked'));
    } else {
      own.hidden = false;
      /*
        Le rang, ET la part devancée quand elle a un sens. Un rang seul se lit mal tant
        que la population est petite : « 12e » est excellent sur quinze joueurs et
        médiocre sur dix mille. Le pourcentage porte ce que le rang cache.
      */
      const place = t('ranking.own', { rank: n(board.ownRank), total: n(board.ranked) });
      own.textContent =
        board.ownPercentile === null
          ? place
          : `${place} — ${t('ranking.percentile', {
              value: n(Math.round(board.ownPercentile * 100)),
            })}`;
      say('', false);
    }

    list.replaceChildren();
    for (const [index, city] of board.top.entries()) {
      const row = document.createElement('li');
      row.className = 'ranking__row';
      /*
        La ville du joueur est SIGNALÉE dans la liste quand elle y figure. Sans ça, un
        joueur du top 50 verrait son nom sans savoir que c'est le sien — les noms par
        défaut se ressemblent tous (« Ma ville »), et c'est justement le cas courant.
      */
      if (slug !== null && city.slug === slug) row.classList.add('ranking__row--own');

      const place = document.createElement('b');
      place.className = 'ranking__place';
      place.textContent = String(index + 1);

      const link = document.createElement('a');
      link.className = 'ranking__name';
      link.href = `/ville/${city.slug}`;
      link.target = '_blank';
      link.rel = 'noopener';
      link.textContent = city.name;

      const era = document.createElement('span');
      era.className = 'ranking__era';
      era.textContent = t('ranking.era', { value: n(city.prestigeLevel + 1) });

      const value = document.createElement('b');
      value.className = 'ranking__value';
      /*
        ⚠️ Un TOTAL, pas un débit — la clé garde son ancien nom pour ne pas casser les
        traductions, son libellé a changé (« pièces produites »).

        Demande de Benoit (22 août), et c'est la bonne métrique pour un idle : la
        production par seconde ne récompense que l'état actuel de la ville, si bien qu'un
        joueur qui vient de prestiger dégringole d'un coup — alors qu'il vient de faire
        exactement ce que le jeu lui demande. `totalProduced` ne recule jamais.
      */
      value.textContent = t('ranking.perSecond', { value: n(city.production) });

      row.append(place, link, era, value);
      list.append(row);
    }
  };

  const load = (slug: string | null): void => {
    if (loading) return;
    loading = true;
    lastSlug = slug;
    reload.el.setAttribute('disabled', '');
    say(t('ranking.loading'));

    void fetchLeaderboard(slug)
      .then((board) => {
        render(board, slug);
      })
      .catch(() => {
        render(null, slug);
      })
      .finally(() => {
        loading = false;
        reload.el.removeAttribute('disabled');
      });
  };

  reload.el.addEventListener('click', () => {
    load(lastSlug);
  });

  say(t('ranking.loading'));

  return {
    el: panel.el,
    refresh(ownSlug) {
      load(ownSlug);
    },
    showState(state) {
      if (state === 'offline') render(null, null);
      else if (state === 'pending') {
        render(
          {
            top: [],
            ownRank: null,
            ownPercentile: null,
            ranked: 0,
            ownUnnamed: false,
            notComputed: true,
          },
          null,
        );
      } else if (state === 'unnamed') {
        render(
          {
            top: [],
            ownRank: null,
            ownPercentile: null,
            ranked: 40,
            ownUnnamed: true,
            notComputed: false,
          },
          null,
        );
      } else
        render(
          {
            top: [],
            ownRank: null,
            ownPercentile: null,
            ranked: 12,
            ownUnnamed: false,
            notComputed: false,
          },
          null,
        );
    },
  };
}
